import { setLocalizedCanvasText } from '../../../util/localizedCanvasText';
import CharacterFactoryV2, { FightRole } from '../../../components/Fight/CharacterFactoryV2';
import Character from '../../../components/Fight/Characters/Character';
import { effects } from '../../../components/Fight/Effects/Effects';
import { Motion } from '../../../components/Fight/Motions/Motion';
import { skills } from '../../../components/Fight/Skills/Skills';
import { FightEvents, FightTurn, UserSkills } from '../../../components/interfaces/Interfaces';
import { createLoader } from '../../../components/phaser/Loader';
import { CharacterAnimation } from '../../../enums';
import store from '../../../store';
import { TopUI } from '../../Combat/Components/TopUI';
import { createCombatAnimation } from '../../../components/Fight/createCombatAnimation';
import { isMobileGameDevice } from '../../../hooks/useMobileGameViewport';
import { installMobileCombatTextures } from '../../../util/mobileCombatTextures';
import { MobileAtlas, selectMobileAtlas } from '../../../util/mobileBattleAssets';

interface FightData {
  fight: FightTurn[];
  roles: FightRole[];
}

export class PreloadScene extends Phaser.Scene {
  private d: any;
  private loaded = new Set<string>();
  private turns: FightTurn[] = [];
  private roles: Character[] = [];
  private fightScene = 1;
  private avatarAnimations: Map<number, Set<string>>;
  private petAnimations=new Map<number,Set<string>>();
  private mobileAtlases=new Map<string,MobileAtlas>();

  usesSecretTechnique(avatar:number) {
    return this.avatarAnimations.get(avatar)?.has(CharacterAnimation.SECRET_TECHNIQUE)??false;
  }

  public motionData: Map<number, any>;
  public skillData: Map<number, any>;
  public petData: Map<number, any>;
  public motions = new Map<number, Motion>();
  public font = '';
  public originalNumbers = false;
  public skillList = new Set(['jutsu', 'mud_wall', 'substitution_technique']);
  public effectList = new Set<string>();

  private cacheTime = 0;

  constructor() {
    super({ key: 'preload' });
  }

  init(data: any) {
    installMobileCombatTextures(this);
    this.cacheTime = Date.now();
    this.d = JSON.parse(JSON.stringify(data));
    this.d.onFinish = data.onFinish;
    this.mobileAtlases=data.mobileAtlases??new Map();
    this.load.maxParallelDownloads=isMobileGameDevice()?2:32;
    this.d.roles.forEach((role: FightRole) => {
      if (CharacterFactoryV2.characterData[role.avatar] === undefined) {
        role.avatar = 1;
      }
    });

    this.fightScene = data.scene ?? 1;
    this.turns = data.fight;

    this.motionData = data.motionData;
    this.skillData = data.skillData;
    this.petData = data.petData;

    const settings = store.getState().settings;
    this.originalNumbers = settings.originalDamage;

    const avatarAnimations = new Map<number, Set<string>>();
    this.petAnimations=new Map();
    const roleIndexToAvatar: { [idx: number]: number } = {};

    this.d.roles.forEach((role: FightRole) => {
      const animationSet = new Set([
        CharacterAnimation.ATTACK,
        CharacterAnimation.DEAD,
        CharacterAnimation.MOVE,
        CharacterAnimation.HURT_LITE,
        CharacterAnimation.IDLE,
        CharacterAnimation.REVIVE,
        CharacterAnimation.TAUNT,
        CharacterAnimation.WIN,
        CharacterAnimation.ZONE_ENTRANCE,
      ]);

      avatarAnimations.set(role.avatar, animationSet);
      if(role.pet)this.petAnimations.set(role.pet,new Set(Array.from(animationSet).concat([CharacterAnimation.BLOCK,'69_1' as CharacterAnimation])));
      roleIndexToAvatar[role.index] = role.avatar;
    });

    const checkSkill = (skill: any, avatar: number, targetAvatar: number) => {
      if (skill === undefined) {
        return;
      }

      if (skill.characterAnimation !== undefined) {
        avatarAnimations.get(avatar)?.add(skill.characterAnimation);
      }

      if (skill.targetAnimation !== undefined) {
        avatarAnimations.get(targetAvatar)?.add(skill.targetAnimation);
      }

      if (skill.jutsu) {
        avatarAnimations.get(avatar)?.add(CharacterAnimation.JUTSU);
      }
      const petAnimation=skill.petAnimation??(skill.onPet?skill.name?.split('/').pop():undefined);
      if(petAnimation!==undefined)this.d.roles.filter((role:FightRole)=>role.avatar===avatar&&role.pet)
        .forEach((role:FightRole)=>this.petAnimations.get(role.pet!)?.add(String(petAnimation)));
      Object.values(skill.events??{}).forEach((event:any)=>{
        if(Array.isArray(event)&&event[0]==='Animation')avatarAnimations.get(avatar)?.add(String(event[1]));
      });

      skill.linked?.forEach((linkedSkill: number) => {
        if (this.skillData.has(linkedSkill)) {
          checkSkill(this.skillData.get(linkedSkill), avatar, targetAvatar);
        }

        if (linkedSkill in skills) {
          checkSkill(skills[linkedSkill], avatar, targetAvatar);
        }
      });
    };

    data.fight.forEach((turn: FightTurn) => {
      if (turn.event === FightEvents.BACK_THROWED) {
        avatarAnimations.get(roleIndexToAvatar[turn.role])?.add(CharacterAnimation.BOMB_KNOCKBACK);
        avatarAnimations.get(roleIndexToAvatar[turn.role ?? -1])?.add(CharacterAnimation.REVIVE);
        avatarAnimations.get(roleIndexToAvatar[turn.targetRole ?? -1])?.add(CharacterAnimation.BOMB_KICK);
      }

      // if (turn.event === FightEvents.BE_DIE) {
      //   animations.get(turn.role)?.add(CharacterAnimation.DEAD);
      // }

      // if (turn.event === FightEvents.VICTORY) {
      //   animations.get(turn.role)?.add(CharacterAnimation.WIN);
      // }

      if (turn.isHit !== undefined && !turn.isHit) {
        avatarAnimations.get(roleIndexToAvatar[turn.targetRole ?? -1])?.add(CharacterAnimation.DODGE);
      }

      if (turn.skillId === undefined) {
        return;
      }

      // Some turns omit targetRole and playback chooses the active opponent.
      // Preload every possible opponent's reaction/counter pose in that case.
      const sourceRole = this.d.roles.find((role: FightRole) => role.index === turn.role);
      const targetAvatars: number[] = turn.targetRole !== undefined ? [roleIndexToAvatar[turn.targetRole]] :
        this.d.roles.filter((role: FightRole) => role.isOnOffense !== sourceRole?.isOnOffense).map((role: FightRole) => role.avatar);
      targetAvatars.forEach(targetAvatar => {
        checkSkill(this.skillData.get(turn.skillId!), roleIndexToAvatar[turn.role], targetAvatar);
        checkSkill(skills[turn.skillId!], roleIndexToAvatar[turn.role], targetAvatar);
        if (turn.targetSkillId !== undefined) {
          checkSkill(this.skillData.get(turn.targetSkillId), targetAvatar, roleIndexToAvatar[turn.role]);
          checkSkill(skills[turn.targetSkillId], targetAvatar, roleIndexToAvatar[turn.role]);
        }
      });
    });

    this.d.roles.forEach((role: FightRole) => {
      if (avatarAnimations.get(role.avatar)?.has(CharacterAnimation.SECRET_TECHNIQUE) &&
          data.avatarData[role.avatar].secretTechnique?.characterAnimation !== undefined) {
        avatarAnimations.get(role.avatar)?.add(data.avatarData[role.avatar].secretTechnique.characterAnimation);
      }

      // if (animations.get(character.fightRole.index)?.has(CharacterAnimation.REVIVE)) {
      //   animations.get(character.fightRole.index)?.add(CharacterAnimation.DEAD);
      // }

      // character.usedAnimations = avatarAnimations.get(character.fightRole.index)!;
    });

    this.avatarAnimations = avatarAnimations;
  }

  loadImage(key: string, path: string, ...args: any[]) {
    if (!this.textures.exists(key)) {
      this.load.image(key, path, ...args);
      this.loaded.add(key);
    }
  }

  loadMultiatlas(key: string, jsonKey: string, ...args: any[]) {
    const originalPath=(this.load.path.replace(/\/?$/,'/')+jsonKey).replace(/^\//,'');
    const prepared=this.mobileAtlases.get(originalPath);
    if(prepared && !this.textures.exists(key)) {
      const avatar=this.d.roles.find((role:FightRole)=>{
        const base=`actorsV2/${CharacterFactoryV2.characterData[role.avatar]?.name}/`;
        return originalPath===base+'avatar.json'||originalPath.startsWith(base+'weapons/');
      });
      const pet=originalPath.match(/^actorsV2\/pets\/(\d+)\/avatar\.json$/);
      const atlas=selectMobileAtlas(prepared,avatar?this.avatarAnimations.get(avatar.avatar):pet?this.petAnimations.get(Number(pet[1])):undefined);
      // This Phaser version documents JSON objects but its File constructor
      // still calls url.indexOf. Use a short-lived JSON Blob URL instead.
      const url=URL.createObjectURL(new Blob([JSON.stringify(atlas)],{type:'application/json'}));
      const release=()=>{
        URL.revokeObjectURL(url);this.load.off('complete',release);
        this.events.off(Phaser.Scenes.Events.SHUTDOWN,release);
      };
      this.load.once('complete',release);this.events.once(Phaser.Scenes.Events.SHUTDOWN,release);
      this.load.multiatlas(key,url,prepared.path,'');
      this.loaded.add(key);return;
    }
    // Animation 60 alone occupies hundreds of MB; the tutorial never uses it.
    // Keep it available for fights that actually request that animation.
    if (isMobileGameDevice() && /actorsV2\/people\/46\/?$/.test(this.load.path) &&
        jsonKey === 'avatar.json' && !this.avatarAnimations.get(46)?.has('60')) {
      jsonKey = 'avatar.mobile-common-v1.json';
    }
    if (isMobileGameDevice() && ((/actorsV2\/people\/(46|49)\/?$/.test(this.load.path) && ['avatar.json','secret_technique.json','secret_technique_1_1.json'].includes(jsonKey)) ||
        (this.load.path.includes('skills/core') && jsonKey === 'great_strength.json'))) {
      jsonKey=jsonKey.replace('.json','.mobile-v1.json');
    }
    if (!this.textures.exists(key)) {
      this.load.multiatlas(key, jsonKey, ...args);
      this.loaded.add(key);
    }
  }

  loadSpritesheet(key: string, path: string, ...args: any[]) {
    if (!this.textures.exists(key)) {
      this.load.spritesheet(key, path, ...args);
      this.loaded.add(key);
    }
  }

  private includeLegacySkill(id: number | undefined, visited = new Set<number>()) {
    if (id === undefined || visited.has(id) || this.skillData.has(id) ||
        [UserSkills.BASIC_ATTACK, UserSkills.PRIORITY_ATTACK, UserSkills.SECRET_TECHNIQUE, 7777, 40002].includes(id)) return;
    visited.add(id);
    const skill = skills[id];
    if (!skill) return;
    this.skillList.add(skill.name);
    skill.linked?.forEach(linked => this.includeLegacySkill(linked, visited));
  }

  preload() {
    createLoader(this);

    if (process.env.NODE_ENV === 'development') {
      this.load.crossOrigin = 'anonymous';
    }
    const localizedLoadingText = this.add
      .text(this.cameras.main.centerX, this.cameras.main.centerY + 50, 'Loading animation sprites', { fontFamily: 'Arial' })
      .setOrigin(0.5);
    setLocalizedCanvasText(localizedLoadingText, 'Loading animation sprites');

    this.load.image(`background-${this.fightScene}`, `scenes/backgrounds/${this.fightScene}_fight.png`);
    // Motion clocks must never depend on a network image or display a missing-texture marker.
    if (!this.textures.exists('custom-blank')) {
      this.textures.createCanvas('custom-blank', 1, 1)?.refresh();
    }
    this.load.setPath(`icons/people/`);
    this.loadMultiatlas('people-icons', 'all.json');
    this.load.setPath(`icons/monsters/`);
    this.loadMultiatlas('monster-icons', 'all.json');
    // this.load.setPath('assets/actors/data/effects');
    // this.load.json('effect-offsets', 'offsets.json');
    this.load.setPath('');

    const effectOffets = this.cache.json.get('effect-offsets');

    this.turns.forEach((turn) => {
      if (turn.addEffect !== undefined) {
        turn.addEffect.forEach(({ id }) => {
          if (!!effectOffets[id]) {
            this.effectList.add('' + id);
            return;
          }

          if (!(id in effects) || effects[id].hidden) {
            return;
          }

          this.effectList.add(effects[id].name);
        });
      }

      this.includeLegacySkill(turn.skillId);
      this.includeLegacySkill(turn.targetSkillId);
    });

    // this.load.setPath('assets/skills/motion/');
    // this.motionData.forEach((data, key) => {
    //   this.load.json(`motion-${key}`, `${key}.json`);
    // });

    this.skillData.forEach((data, key) => {
      this.load.setPath(`skills/${data.name.substring(0, data.name.indexOf('/') + 1)}`);
      this.loadMultiatlas(`skill-${key}`, `${data.name.substring(data.name.lastIndexOf('/') + 1, data.name.length)}.json`);
    });

    this.load.setPath(`skills/core/`);
    this.skillList.forEach((skill) => {
      this.loadMultiatlas(`skill-${skill}`, `${skill}.json`);
    });

    this.load.setPath('actors/effects/');
    this.effectList.forEach((effect) => {
      this.loadMultiatlas(`effect-${effect}`, `${effect}.json`);
    });

    if (this.originalNumbers) {
      this.load.setPath('actors/numbers/');
      this.loadMultiatlas('damage-normal', 'normal.json');
      this.loadMultiatlas('damage-heal', 'heal.json');
    }

    this.load.setPath(`icons/`);
    this.loadMultiatlas('icons-effects', 'effects.json');
    this.loadMultiatlas('icons-skills', 'skills.json');

    this.load.setPath('');

    this.d.roles.map((role: FightRole) => {
      try {
        CharacterFactoryV2.load(this, role);
      } catch (e) {
        console.error(e);
        throw Error('Stop');
      }
    });

    TopUI.load(this);
  }

  create() {
    // this.customPipeline = (this.game.renderer as Phaser.Renderer.WebGL.WebGLRenderer).addPipeline('Gray', new GrayscalePipeline(this.game));
    // this.customPipeline.setFloat2('uResolution', this.game.config.width, this.game.config.height);
    // (this.game.renderer as Phaser.Renderer.WebGL.WebGLRenderer).addPipeline(OutlinePipeline.KEY, new OutlinePipeline(this.game));

    const skillList = this.skillList;

    this.motionData.forEach((data, key) => this.motions.set(key, new Motion(this, data)));
    this.skillData.forEach((data, key) => {
      if (data.noSprite) {
        return;
      }

      createCombatAnimation(this, '' + key, `skill-${key}`, 0, Object.keys(data.events || {}).map(Number));
    });

    skillList.forEach((skill) => {
      const data = Object.values(skills).find(data => data.name === skill);
      createCombatAnimation(this, skill, `skill-${skill}`, 1, Object.keys(data?.events || {}).map(Number));
    });

    this.effectList.forEach((effect) => {
      createCombatAnimation(this, `effect-${effect}`, `effect-${effect}`, 1, [], -1);
    });

    this.data.set('effectList', this.effectList);
    this.data.set('skillList', this.skillList);

    this.d.roles.map((role: FightRole) => CharacterFactoryV2.createAnimations(this, role, this.avatarAnimations.get(role.avatar)));

    this.scene.start('combatPlayer', { ...this.d, skillData: this.skillData });
  }
}


